import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, UserCheck, UserX, CheckCircle2, MessageSquare, Inbox } from "lucide-react";
import { API, getAuthHeaders } from "@/App";

const STATUS_LABEL = {
  open: { label: "Abierto", color: "bg-blue-600" },
  claimed: { label: "Atendiendo", color: "bg-amber-600" },
  resolved: { label: "Resuelto", color: "bg-emerald-600" },
};

export default function TeamInboxView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open");

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/team/inbox`, { credentials: "include", headers: getAuthHeaders() });
      if (r.ok) setItems(await r.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const action = async (cid, kind) => {
    try {
      const r = await fetch(`${API}/team/inbox/${cid}/${kind}`, {
        method: "POST",
        credentials: "include",
        headers: getAuthHeaders(),
      });
      if (!r.ok) throw new Error();
      toast.success(kind === "claim" ? "Tomaste la conversación" : kind === "release" ? "Liberada" : "Resuelta");
      load();
    } catch {
      toast.error("Acción fallida");
    }
  };

  const filtered = items.filter((c) => (filter === "all" ? true : (c.team_status || "open") === filter));

  return (
    <Card className="bg-[#17212b] border-[#232e3c]">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <Inbox className="w-5 h-5 text-blue-400" /> Bandeja del equipo
            </CardTitle>
            <CardDescription className="text-slate-400">
              Cualquier miembro puede tomar, liberar o resolver una conversación.
            </CardDescription>
          </div>
          <div className="flex gap-1">
            {["open", "claimed", "resolved", "all"].map((k) => (
              <Button
                key={k}
                variant={filter === k ? "default" : "ghost"}
                size="sm"
                onClick={() => setFilter(k)}
                className={filter === k ? "bg-blue-600" : "text-slate-300"}
              >
                {k === "all" ? "Todas" : STATUS_LABEL[k]?.label || k}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-400" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-40" />
            Sin conversaciones en este estado.
          </div>
        ) : (
          <ul className="divide-y divide-[#232e3c]">
            {filtered.map((c) => {
              const st = STATUS_LABEL[c.team_status || "open"] || STATUS_LABEL.open;
              const ou = c.other_user || {};
              return (
                <li key={c.conversation_id} className="py-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#232e3c] flex items-center justify-center overflow-hidden text-white">
                    {ou.picture ? (
                      <img src={ou.picture} alt={ou.name} className="w-full h-full object-cover" />
                    ) : (
                      (ou.name || "?")[0]
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="text-white font-medium truncate">{ou.name || "Cliente"}</div>
                      <Badge className={`${st.color} text-white`}>{st.label}</Badge>
                      {c.team_assigned_to && (
                        <span className="text-xs text-slate-400">
                          → {c.team_assigned_name || c.team_assigned_to.slice(0, 8)}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-400 truncate">
                      {c.last_message?.content || "Sin mensajes recientes"}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {(c.team_status || "open") === "open" && (
                      <Button size="sm" onClick={() => action(c.conversation_id, "claim")} className="bg-blue-600 hover:bg-blue-700">
                        <UserCheck className="w-4 h-4 mr-1" /> Tomar
                      </Button>
                    )}
                    {(c.team_status || "open") === "claimed" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => action(c.conversation_id, "release")}>
                          <UserX className="w-4 h-4 mr-1" /> Liberar
                        </Button>
                        <Button size="sm" onClick={() => action(c.conversation_id, "resolve")} className="bg-emerald-600 hover:bg-emerald-700">
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Resolver
                        </Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => (window.location.href = `/chat?c=${c.conversation_id}`)}>
                      Abrir
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
